import { CommonModule, DatePipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, DestroyRef, EventEmitter, Input, Output, inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatMenuModule } from '@angular/material/menu';
import { SelectionModel } from '@angular/cdk/collections';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { of, catchError, forkJoin, firstValueFrom } from 'rxjs';
import { Bundle, ServiceRequest, Specimen } from 'fhir/r4';

import { backendEndPointToken } from '../../app.config';
import { AddSpecimenComponent } from '../../specimen/add-specimen/add-specimen.component';
import { AuthService } from '../../shared/auth/auth.service';
import { ConfirmDialogComponent } from '../../shared/confirm-dialog/confirm-dialog.component';
import { ErrorService } from '../../shared/error.service';
import { FhirResourceService } from '../../shared/fhir-resource.service';
import { FhirResourceTransformService } from '../../shared/fhir-resource-transform.service';
import { StateService } from '../../shared/state.service';
import { UtilityService } from '../../shared/utility.service';
import { NaPipe } from '../../shared/na.pipe';
import { ChipsDirective } from '../../chips.directive';
import { combineDateAndTimeToIso } from '../../shared/date-time-utils';
import { MatTooltip, MatTooltipModule } from '@angular/material/tooltip';
import { SpecimenContainerLabelPrintService } from '../../shared/specimen-container-label-print.service';
import { ReferenceDisplayService } from '../../shared/reference-display.service';

export type SpecimenSelectionDialogData = {
    serviceRequestIds?: string[];
    specimensToMatch?: Specimen[];
    forSelection?: boolean
    title?: string;
};

@Component({
    selector: 'app-specimen-selection',
    standalone: true,
    imports: [
        CommonModule,
        MatDialogModule,
        NaPipe,
        ChipsDirective,
        DatePipe,
        MatTableModule,
        MatCheckboxModule,
        MatButtonModule,
        MatIconModule,
        MatMenuModule,
        MatTooltipModule,
        MatProgressSpinnerModule
    ],
    templateUrl: './specimen-selection.component.html',
    styleUrl: './specimen-selection.component.scss'
})
export class SpecimenSelectionComponent {
    private readonly http = inject(HttpClient);
    private readonly backendApiBaseUrl = inject(backendEndPointToken);
    private readonly destroyRef = inject(DestroyRef);
    private readonly dialog = inject(MatDialog);
    private readonly snackBar = inject(MatSnackBar);
    private readonly auth = inject(AuthService);
    private readonly errorService = inject(ErrorService);
    private readonly fhirTransformService = inject(FhirResourceTransformService);
    private readonly fhirResourceService = inject(FhirResourceService);
    private readonly stateService = inject(StateService);
    private readonly utilityService = inject(UtilityService);
    private readonly refDisplay = inject(ReferenceDisplayService);

    private readonly dialogRef = inject(MatDialogRef<SpecimenSelectionComponent>, { optional: true }) as MatDialogRef<SpecimenSelectionComponent> | null;
    private readonly dialogData = inject(MAT_DIALOG_DATA, { optional: true }) as SpecimenSelectionDialogData | null;

    @Input() serviceRequestIds: string[] = [];
    @Input() specimensToMatch: Specimen[] = [];
    @Input() forSelection: boolean = true;

    @Output() selectionChange = new EventEmitter<Specimen[]>();

    title = 'Select Specimen';
    loading = false;
    adding = false;
    printingService = inject(SpecimenContainerLabelPrintService);

    private expectedServiceRequestIds: string[] = [];
    private readonly serviceRequestStatusById = new Map<string, string>();
    serviceRequestCompletionLoading = false;

    get editingLockedByServiceRequestCompletion(): boolean {
        if (!this.expectedServiceRequestIds.length) return false;
        for (const id of this.expectedServiceRequestIds) {
            const st = (this.serviceRequestStatusById.get(id) ?? '').toString().trim().toLowerCase();
            if (!st) return false; // unknown; do not lock
            if (st !== 'completed') return false;
        }
        return true;
    }

    private extractIdFromReference(ref?: string | null, expectedType?: string): string | null {
        const raw = (ref ?? '').toString().trim();
        if (!raw) return null;
        const noFrag = raw.split('#')[0].split('?')[0];
        const parts = noFrag.split('/').filter(Boolean);
        if (!parts.length) return null;
        if (expectedType) {
            const idx = parts.lastIndexOf(expectedType);
            if (idx >= 0 && parts[idx + 1]) return parts[idx + 1];
            return null;
        }
        return parts[parts.length - 1] || null;
    }

    private resolveServiceRequestCompletionState(): void {
        const ids = (this.serviceRequestIds ?? [])
            .map((x) => (x ?? '').toString().trim())
            .filter(Boolean);

        this.expectedServiceRequestIds = Array.from(new Set(ids));
        this.serviceRequestStatusById.clear();
        if (!this.expectedServiceRequestIds.length) return;

        // Best-effort: use state cache first.
        try {
            const inState = (this.stateService.orgWideResources.serviceRequests.getValue() ?? [])
                .map((x: any) => x?.actualResource)
                .filter(Boolean) as any[];
            for (const sr of inState) {
                const id = (sr?.id ?? '').toString().trim();
                if (id && this.expectedServiceRequestIds.includes(id)) {
                    const st = (sr?.status ?? '').toString();
                    if (st) this.serviceRequestStatusById.set(id, st);
                }
            }
        } catch {
            // ignore
        }

        const missing = this.expectedServiceRequestIds.filter((id) => !this.serviceRequestStatusById.has(id));
        if (!missing.length) return;

        this.serviceRequestCompletionLoading = true;
        forkJoin(
            missing.map((id) =>
                this.http.get<ServiceRequest>(`${this.backendApiBaseUrl}/ServiceRequest/${encodeURIComponent(id)}`).pipe(
                    catchError(() => of(null as any))
                )
            )
        )
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((list: Array<ServiceRequest | null>) => {
                this.serviceRequestCompletionLoading = false;
                for (const sr of (list ?? []).filter(Boolean) as ServiceRequest[]) {
                    const id = (sr?.id ?? '').toString().trim();
                    if (!id) continue;
                    const st = (sr as any)?.status;
                    if (st) this.serviceRequestStatusById.set(id, st);
                }
            });
    }

    private codeableConceptToText(cc: any): string {
        if (!cc) return '';
        if (typeof cc === 'string') return cc;
        const coding = Array.isArray(cc.coding) ? cc.coding : [];
        return cc.text || coding[0]?.display || coding[0]?.code || '';
    }

    private formatIsoDateTime(iso?: string | null): string | undefined {
        if (!iso) return undefined;
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return iso;
        return d.toLocaleString();
    }

    private extractContainerId(specimen: Specimen | any): string {
        const s: any = specimen as any;

        // Most common: Specimen.container[0].identifier is an Identifier
        const ident = s?.container?.[0]?.identifier;
        if (ident) {
            if (Array.isArray(ident)) {
                const firstWithValue = ident.find((x: any) => x?.value) ?? ident[0];
                const v = firstWithValue?.value;
                if (v != null && String(v).trim()) return String(v).trim();
            } else {
                const v = ident?.value;
                if (v != null && String(v).trim()) return String(v).trim();
            }
        }

        // Fallbacks seen in some servers/mappers
        const alt1 = s?.container?.[0]?.identifier?.[0]?.value;
        if (alt1 != null && String(alt1).trim()) return String(alt1).trim();

        const alt2 = s?.identifier?.[0]?.value ?? s?.identifier?.value;
        if (alt2 != null && String(alt2).trim()) return String(alt2).trim();

        return '';
    }

    async printLabel(row: Specimen): Promise<void> {
        if (!row?.id) {
            this.errorService.openandCloseError('Cannot print label: specimen has no id.');
            return;
        }

        const patientRef = (row?.subject as any)?.reference ? String((row?.subject as any).reference) : '';
        const serviceRequestRef = (row?.request?.[0] as any)?.reference ? String((row?.request?.[0] as any).reference) : '';

        const patientId = patientRef ? (patientRef.split('/').pop() ?? 'unknown') : 'unknown';
        const serviceRequestId = serviceRequestRef ? (serviceRequestRef.split('/').pop() ?? 'unknown') : 'unknown';

        const receivedTimeIso = (row as any)?.receivedTime ?? (row as any)?.collection?.collectedDateTime ?? '';
        const receivedTime = this.formatIsoDateTime(receivedTimeIso ? String(receivedTimeIso) : null);
        const containerId = this.extractContainerId(row);
        const specimenType = this.codeableConceptToText((row as any)?.type);

        let patientName = '';
        let serviceRequestName = '';
        try {
            const resolved = await firstValueFrom(
                forkJoin({
                    patient: patientRef
                        ? this.refDisplay.ensure(patientRef, { displayPresent: (row?.subject as any)?.display || null })
                        : of(''),
                    serviceRequest: serviceRequestRef
                        ? this.refDisplay.ensure(serviceRequestRef, { displayPresent: (row?.request?.[0] as any)?.display || null })
                        : of('')
                })
            );

            patientName = (resolved.patient || '').toString();
            serviceRequestName = (resolved.serviceRequest || '').toString();
        } catch {
            // ignore and fall back to ids
        }

        this.printingService.printSmallLabel({
            patientPrimaryId: patientId,
            patientName: patientName || undefined,
            serviceRequestId,
            serviceRequestName: serviceRequestName || undefined,
            specimenType: specimenType || undefined,
            receivedTime: receivedTime || undefined,
            containerId: row.id ? String(row.id) : undefined
        });


    }

    private readonly updatingStatusIds = new Set<string>();

    dataSource = new MatTableDataSource<Specimen>([]);
    selection = new SelectionModel<Specimen>(true, []);

    tableColumns: string[] = ['select', 'type', 'status', 'receivedTime'];

    get canAddSpecimen(): boolean {
        return this.auth.can('specimen', 'add');
    }

    get canEditSpecimen(): boolean {
        return this.auth.can('specimen', 'edit');
    }

    get isSingleServiceRequest(): boolean {
        return (this.serviceRequestIds ?? []).length === 1;
    }

    get singleServiceRequestId(): string | null {
        return this.isSingleServiceRequest ? (this.serviceRequestIds?.[0] ?? null) : null;
    }

    ngOnInit(): void {
        const d = this.dialogData;
        if (d?.title) this.title = d.title;

        if ((!this.serviceRequestIds || this.serviceRequestIds.length === 0) && Array.isArray(d?.serviceRequestIds)) {
            this.serviceRequestIds = d?.serviceRequestIds ?? [];
        }
        if ((!this.specimensToMatch || this.specimensToMatch.length === 0) && Array.isArray(d?.specimensToMatch)) {
            this.specimensToMatch = d?.specimensToMatch ?? [];
        }
        if (d?.hasOwnProperty('forSelection') && typeof d.forSelection === 'boolean') {
            this.forSelection = d.forSelection;
        }

        this.resolveServiceRequestCompletionState();


        this.selection.changed
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.selectionChange.emit([...this.selection.selected]));

        this.rebuildColumns();

        this.refresh();
    }

    private rebuildColumns(): void {
        this.tableColumns = this.forSelection ? ['select', 'type', 'status', 'receivedTime'] : ['type', 'status', 'receivedTime'];
        // Show actions column in single-service-request mode; buttons handle permission gating.
        if (this.isSingleServiceRequest) this.tableColumns.push('actions');
    }

    close(): void {
        this.dialogRef?.close();
    }

    confirm(): void {
        this.dialogRef?.close({ selectedSpecimens: [...this.selection.selected] });
    }

    addSpecimen(): void {
        const serviceRequestId = this.singleServiceRequestId;
        if (!serviceRequestId) return;
        if (!this.canAddSpecimen) {
            this.errorService.openandCloseError('You do not have permission to add specimens.');
            return;
        }
        this.adding = true;

        this.http.get<ServiceRequest>(`${this.backendApiBaseUrl}/ServiceRequest/${serviceRequestId}`).pipe(
            catchError((err) => {
                console.error(err);
                this.errorService.openandCloseError('Failed to load Service Request for specimen creation.');
                return of(null as any);
            })
        ).subscribe((sr: any) => {
            if (!sr) {
                this.adding = false;
                return;
            }

            const subjectRef = (sr?.subject?.reference ?? '').toString();
            const patientId = this.extractPatientId(subjectRef);

            const ref = this.dialog.open(AddSpecimenComponent, {
                maxHeight: '90vh',
                maxWidth: '650px',
                autoFocus: false,
                data: { patientId, serviceRequestId }
            });

            ref.afterClosed().subscribe((result: any) => {
                this.adding = false;

                if (!result || !result.values || (Array.isArray(result.values) && result.values.length === 0)) {
                    return;
                }

                let values = result.values;
                if (!Array.isArray(values)) values = [values];

                const practitionerId = this.auth.user.getValue()?.['fhirResourceId'];
                const practitionerRef = practitionerId ? `Practitioner/${practitionerId}` : null;

                const usedContainerIdentifierValues = new Set<string>();

                const specimenResources = (values || []).map((val: any) => {
                    // Unique container identifier per specimen (client-generated)
                    let containerIdentifier = this.utilityService.generateLocalIdentifier({
                        system: 'urn:elikita:specimen-container',
                        prefix: 'container'
                    });
                    while (usedContainerIdentifierValues.has((containerIdentifier.value ?? '').toString())) {
                        containerIdentifier = this.utilityService.generateLocalIdentifier({
                            system: 'urn:elikita:specimen-container',
                            prefix: 'container'
                        });
                    }
                    usedContainerIdentifierValues.add((containerIdentifier.value ?? '').toString());

                    // const resource: any = {
                    //     ...this.fhirTransformService.transformValues('Specimen', val),
                    //     resourceType: 'Specimen'
                    // };
                    // status., type, subject, request, receivedTime, ondition, bodySite, note
                    const resource: Specimen = {
                        resourceType: 'Specimen',
                        status: this.normalizeSpecimenStatus(val.status),
                        type: this.utilityService.toCodeableConcept(val.type),
                        subject: this.fhirTransformService.toReference(val.subject),
                        request: [this.fhirTransformService.toReference(val.request)],
                        container: [
                            {
                                identifier: containerIdentifier
                            } as any
                        ],
                        ...(val.receivedTime ? {
                            receivedTime: combineDateAndTimeToIso(val.receivedTime, val.receivedTimeTime) ?? val.receivedTime,
                        } : {}),
                        ...(val.condition ? { condition: (Array.isArray(val.condition) ? val.condition : [val.condition]).filter((e: any) => e !== "" && String(e).trim() !== "").map((c: any) => this.fhirTransformService.toCodeableConcept(c)) } : {}),
                        //note?
                        ...(val.note ? { note: Array.isArray(val.note) ? val.note.map((n: any) => ({ text: n })) : [{ text: val.note }] } : {})
                    }
                    if (practitionerRef) {
                        resource.collection = {
                            ...(resource.collection || {}),
                            collector: { reference: practitionerRef }
                        };
                    }
                    //  keys: ['isCollectedOverPeriod', 'collectionStart', 'collectionEnd', 'collectionDateTime'],
                    if (val.collectedTime && val.collectedTime.isCollectedOverPeriod && (val.collectedTime?.collectionDateTime || (val.collectedTime?.collectionStart && val.collectedTime?.collectionEnd))) {
                        resource.collection = {
                            ...(resource.collection || {}),
                            ...(String(val.collectedTime.isCollectedOverPeriod).toLowerCase() === 'no' && val.collectedTime.collectionDateTime ? {
                                collectedDateTime: combineDateAndTimeToIso(
                                    val.collectedTime.collectionDateTime,
                                    val.collectedTime.collectionTime
                                )
                            } : {}),
                            ...(String(val.collectedTime.isCollectedOverPeriod).toLowerCase() === 'yes' && val.collectedTime.collectionStart && val.collectedTime.collectionEnd ? {
                                collectedPeriod: {
                                    start: combineDateAndTimeToIso(
                                        val.collectedTime.collectionStart,
                                        val.collectedTime.collectionStartTime
                                    ),
                                    end: combineDateAndTimeToIso(
                                        val.collectedTime.collectionEnd,
                                        val.collectedTime.collectionEndTime
                                    )
                                }
                            } : {}),
                            //if bodySite is present, add it to the resource
                            ...(val.bodySite ? { bodySite: this.utilityService.toCodeableConcept(val.bodySite) } : {}),
                        };

                    }
                    console.log('Transformed specimen resource to be created:', resource);
// $#$                    // /* debugger; */
                    return resource;
                });

                const bundle: Bundle<any> = {
                    resourceType: 'Bundle',
                    type: 'transaction',
                    entry: specimenResources.map((resource: any) => ({
                        resource,
                        request: { method: 'POST', url: 'Specimen' }
                    }))
                };

                this.fhirResourceService.postBundle(bundle).pipe(
                    catchError((e) => {
                        console.error(e);
                        this.errorService.openandCloseError('Error creating specimen record(s).');
                        return of(null as any);
                    })
                ).subscribe((response) => {
                    const persistedResources =
                        response?.bundle?.entry?.map((entry: any) => entry.resource).filter(Boolean) ?? specimenResources;

                    for (const r of persistedResources) {
                        this.stateService.persistOrgWideResource(r, 'saved');
                    }

                    this.snackBar.open('Specimen created.', 'OK', { duration: 2500 });
                    this.refresh();
                });
            });
        });
    }

    requestSetStatus(row: Specimen, newStatus: 'available' | 'unavailable' | 'unsatisfactory' | 'entered-in-error'): void {
        if (!this.canEditSpecimen) {
            this.errorService.openandCloseError('You do not have permission to edit specimens.');
            return;
        }

        if (newStatus === 'entered-in-error') {
            const ref = this.dialog.open(ConfirmDialogComponent, {
                maxWidth: '520px',
                data: {
                    title: 'Mark specimen as entered-in-error?',
                    message: 'This should be used only when the specimen was created by mistake. Continue?',
                    confirmText: 'Mark entered-in-error',
                    cancelText: 'Cancel',
                    icon: 'warning'
                }
            });

            ref.afterClosed().subscribe((confirmed: boolean) => {
                if (confirmed === true) this.setStatus(row, newStatus);
            });
            return;
        }

        this.setStatus(row, newStatus);
    }

    private setStatus(row: Specimen, newStatus: 'available' | 'unavailable' | 'unsatisfactory' | 'entered-in-error'): void {
        const id = (row as any)?.id?.toString?.() ?? '';
        if (!id) {
            this.errorService.openandCloseError('Cannot change status: specimen has no id.');
            return;
        }

        if (this.updatingStatusIds.has(id)) return;
        this.updatingStatusIds.add(id);

        const updated: any = { ...(row as any), status: newStatus };
        const headers = { 'Content-Type': 'application/fhir+json', Accept: 'application/fhir+json', Prefer: 'return=representation' };

        this.http.put<Specimen>(`${this.backendApiBaseUrl}/Specimen/${id}`, updated, { headers }).pipe(
            catchError((err) => {
                console.error(err);
                this.errorService.openandCloseError('Failed to update specimen status.');
                return of(null as any);
            })
        ).subscribe((saved: any) => {
            this.updatingStatusIds.delete(id);
            if (!saved) return;

            this.stateService.persistOrgWideResource(saved as any, 'saved');

            // Update current table row in-place
            const next = (this.dataSource.data ?? []).map((s: any) => ((s?.id ?? '').toString() === id ? saved : s));
            this.dataSource.data = next;
        });
    }

    isUpdatingStatus(row: Specimen): boolean {
        const id = (row as any)?.id?.toString?.() ?? '';
        return id ? this.updatingStatusIds.has(id) : false;
    }

    refresh(): void {
        const ids = (this.serviceRequestIds ?? []).map((x) => (x ?? '').toString().trim()).filter(Boolean);
        this.loading = true;
        this.rebuildColumns();

        const apiBase = (this.backendApiBaseUrl ?? '').toString().replace(/\/+$/, '');
        const srRefSet = new Set<string>();
        for (const id of ids) {
            srRefSet.add(`ServiceRequest/${id}`);
            if (apiBase) srRefSet.add(`${apiBase}/ServiceRequest/${id}`);
        }
        const srRefs = Array.from(srRefSet);
        const urlSearchObject = srRefs.length ? { request: srRefs } : {};

        // Enforce OR at the client side too: include a specimen if it references
        // any of the passed ServiceRequest refs.
        const querySearchObject = srRefs.length ? { key: 'request', value: srRefs } : undefined;

        this.utilityService.threeTierQueryAndSyncToStateOnceOr$<Specimen>({
            resourceType: 'Specimen',
            scope: 'orgWide',
            urlSearchObject,
            urlOrKeys: srRefs.length ? ['request'] : undefined,
            querySearchObject,
            count: 200,
            format: 'json'
        }).pipe(
            takeUntilDestroyed(this.destroyRef)
        ).subscribe({
            next: (specimens) => {
                const flat = specimens ?? [];
                const byId = new Map<string, Specimen>();
                for (const s of flat) {
                    const id = (s as any)?.id?.toString?.() ?? '';
                    const key = id ? `Specimen/${id}` : JSON.stringify(s);
                    if (!byId.has(key)) byId.set(key, s);
                }
                this.dataSource.data = Array.from(byId.values());
                this.applyPreselection();
                this.loading = false;
            },
            error: () => {
                this.dataSource.data = [];
                this.selection.clear();
                this.loading = false;
            }
        });
    }

    private applyPreselection(): void {
        const toMatch = this.specimensToMatch ?? [];
        if (!toMatch.length) return;

        const ids = new Set(toMatch.map((s: any) => (s?.id ?? '').toString()).filter(Boolean));
        if (!ids.size) return;

        this.selection.clear();
        for (const row of this.dataSource.data) {
            const id = (row as any)?.id?.toString() ?? '';
            if (id && ids.has(id)) this.selection.select(row);
        }
    }

    isAllSelected(): boolean {
        const numSelected = this.selection.selected.length;
        const numRows = this.dataSource.data.length;
        return numRows > 0 && numSelected === numRows;
    }

    toggleAllRows(): void {
        if (this.isAllSelected()) {
            this.selection.clear();
            return;
        }
        this.selection.select(...this.dataSource.data);
    }

    checkboxLabel(row?: Specimen): string {
        if (!row) {
            return `${this.isAllSelected() ? 'deselect' : 'select'} all`;
        }
        return `${this.selection.isSelected(row) ? 'deselect' : 'select'} row`;
    }

    getType(s: any): string {
        return s?.type?.text ?? s?.type?.coding?.[0]?.display ?? s?.type?.coding?.[0]?.code ?? '—';
    }

    getStatus(s: any): string {
        return s?.status ?? '—';
    }

    getReceivedTime(s: any): string {
        return s?.receivedTime ?? s?.collection?.collectedDateTime ?? '—';
    }

    private normalizeSpecimenStatus(status: unknown): Specimen['status'] {
        if (typeof status !== 'string') return undefined;

        const s = status.trim().toLowerCase();
        switch (s) {
            case 'available':
            case 'unavailable':
            case 'unsatisfactory':
            case 'entered-in-error':
                return s as Specimen['status'];
            default:
                return undefined;
        }
    }

    private extractPatientId(subjectRef: string): string | undefined {
        const ref = (subjectRef ?? '').toString();
        if (!ref) return undefined;

        // Handles: Patient/123 and absolute refs ending with Patient/123
        const marker = 'Patient/';
        const idx = ref.lastIndexOf(marker);
        if (idx < 0) return undefined;
        const id = ref.slice(idx + marker.length).split(/[/?#]/)[0];
        return id || undefined;
    }
}
